// UploadCard.tsx
import React, { useState } from 'react';
import {
  Stack,
  Text,
  PrimaryButton,
  Spinner,
  MessageBar,
  MessageBarType,
  TooltipHost,
  Dropdown,
  IDropdownOption,
} from '@fluentui/react';

interface UploadCardProps {
  title: string;
  description: string;
  allowedExtensions: string[];
  uploadHandler: (file: File, plan?: string) => Promise<any>;
  plans?: string[]; // Optional list of plans, e.g. ['420', '430', '440']
}

const stackTokens = { childrenGap: 20, padding: 20 };

const columnStyle = {
  root: {
    border: '1px solid #ccc',
    padding: 20,
    borderRadius: 5,
    width: '100%',
    maxWidth: '800px',
    boxSizing: 'border-box' as const,
  },
};

const UploadCard: React.FC<UploadCardProps> = ({
  title,
  description,
  allowedExtensions,
  uploadHandler,
  plans,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadMessage, setUploadMessage] = useState<string>('');
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  // Validate file extension on change
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
        alert(
          `Formato inválido. Solo se permiten archivos: ${allowedExtensions
            .map((ext) => '.' + ext)
            .join(', ')}`
        );
        return;
      }
      setSelectedFile(file);
    }
  };

  // Trigger the provided API call with the file and (if applicable) the selected plan
  const handleUpload = async () => {
    if (plans && !selectedPlan) {
      alert('Seleccione un plan para continuar.');
      return;
    }

    if (selectedFile) {
      setUploading(true);
      setUploadMessage('');
      try {
        // Pass the selectedFile along with selectedPlan (if any)
        const response = await uploadHandler(
          selectedFile,
          selectedPlan ?? undefined
        );
        if (response.status === 202) {
          setUploadMessage('Archivo subido exitosamente.');
        } else {
          setUploadMessage('Error al subir el archivo.');
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        setUploadMessage('Error al subir el archivo.');
      } finally {
        setUploading(false);
      }
    } else {
      alert('Seleccione un archivo para subir.');
    }
  };

  const planOptions: IDropdownOption[] = plans
    ? plans.map((plan) => ({ key: plan, text: plan }))
    : [];

  return (
    <Stack tokens={stackTokens} styles={columnStyle}>
      <Text variant="large">{title}</Text>
      <Text variant="medium">{description}</Text>
      <Text variant="medium">
        Selecciona un archivo con la extensión permitida:{' '}
        {allowedExtensions.map((ext) => '.' + ext).join(', ')}
      </Text>

      <TooltipHost
        content={`Formato permitido: ${allowedExtensions
          .map((ext) => '.' + ext)
          .join(', ')}`}
        styles={{ root: { display: 'inline-block' } }}
      >
        <input
          type="file"
          accept={allowedExtensions.map((ext) => '.' + ext).join(',')}
          onChange={handleFileChange}
        />
      </TooltipHost>

      {plans && (
        <Dropdown
          label="Seleccione un plan"
          placeholder="Elija una opción"
          options={planOptions}
          selectedKey={selectedPlan ?? undefined}
          onChange={(event, option) =>
            setSelectedPlan(option ? (option.key as string) : null)
          }
        />
      )}

      <Stack horizontalAlign="center">
        <PrimaryButton
          text="Subir Archivo"
          onClick={handleUpload}
          disabled={uploading}
          styles={{ root: { minWidth: '150px', maxWidth: '250px' } }}
        />
      </Stack>
      {uploading && <Spinner label="Subiendo archivo..." />}
      {uploadMessage && (
        <MessageBar
          messageBarType={
            uploadMessage.includes('Error')
              ? MessageBarType.error
              : MessageBarType.success
          }
        >
          {uploadMessage}
        </MessageBar>
      )}
    </Stack>
  );
};

export default UploadCard;
